import { motion } from 'framer-motion';
import { CheckCircle2, Calendar } from 'lucide-react';

export default function CancelSuccessPage() {
  const params = new URLSearchParams(window.location.search);
  const bookingId = params.get('bookingId');
  const refundStatus = params.get('refundStatus');
  const refundAmount = params.get('refundAmount');

  const getRefundMessage = (): string => {
    const amount = refundAmount ? `${refundAmount} €` : null;

    switch (refundStatus) {
      case 'full':
        return amount
          ? `Die Rückerstattung in Höhe von ${amount} wurde vollständig veranlasst.`
          : 'Die Rückerstattung wurde vollständig veranlasst.';
      case 'half':
        return amount
          ? `Die Rückerstattung in Höhe von ${amount} wurde gemäß den Stornierungsbedingungen teilweise veranlasst.`
          : 'Die Rückerstattung wurde gemäß den Stornierungsbedingungen teilweise veranlasst.';
      case 'none':
        return 'Die Buchung wurde storniert. Gemäß den Stornierungsbedingungen erfolgt keine Rückerstattung.';
      default:
        return 'Die Stornierung wurde erfolgreich verarbeitet.';
    }
  };

  const showRefundNote = refundStatus === 'full' || refundStatus === 'half';
  const hasRefundInfo = refundStatus === 'full' || refundStatus === 'half' || refundStatus === 'none';

  return (
    <div className="min-h-screen svh-canvas flex flex-col items-center justify-center px-4 py-16">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-[560px]"
      >
        {/* Brand header */}
        <div className="text-center mb-8">
          <img
            src="/photos/Image.jpeg"
            alt="SV Heinersreuth"
            className="h-14 w-14 rounded-full object-cover mx-auto mb-3 shadow-sm ring-1 ring-black/5"
          />
          <p className="text-[15px] font-bold tracking-tight" style={{ color: 'var(--svh-ink)' }}>
            SV Heinersreuth
          </p>
          <p className="svh-label mt-1">Padel &amp; Tennis</p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="svh-card svh-card-lg svh-card-raised overflow-hidden"
        >
          <div className="h-1 w-full" style={{ background: 'var(--svh-crest)' }} />
          <div className="p-8 md:p-10">
            {/* Success icon */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="flex items-center justify-center mb-6"
            >
              <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" strokeWidth={1.75} />
              </div>
            </motion.div>

            {/* Headline */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.25 }}
              className="text-center mb-8"
            >
              <p className="text-[12px] font-bold tracking-[0.16em] uppercase text-emerald-700 mb-3">
                Stornierung abgeschlossen
              </p>
              <h1 className="svh-h2 mb-4">Ihre Buchung wurde storniert.</h1>
              <p className="svh-body">
                Wir freuen uns, Sie bald wieder auf dem Platz zu sehen.
              </p>
            </motion.div>

            {/* Refund summary */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
              className="rounded-[10px] px-5 py-4 mb-4"
              style={{ background: 'var(--svh-surface-sub)', border: '1px solid var(--svh-border)' }}
            >
              {hasRefundInfo && <p className="svh-label mb-1.5">Rückerstattung</p>}
              <p className="svh-body" style={{ color: 'var(--svh-ink)' }}>
                {getRefundMessage()}
              </p>
            </motion.div>

            {/* Booking reference */}
            {bookingId && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.35 }}
                className="rounded-[10px] px-5 py-4 mb-6"
                style={{ background: 'var(--svh-surface-sub)', border: '1px solid var(--svh-border)' }}
              >
                <div className="flex items-start gap-3">
                  <Calendar className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--svh-ink-3)' }} />
                  <div className="flex-1 min-w-0">
                    <p className="svh-label mb-1">Buchungsreferenz</p>
                    <p className="font-mono text-[13px] leading-relaxed break-all" style={{ color: 'var(--svh-ink-2)' }}>
                      {bookingId}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {showRefundNote && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.4 }}
                className="svh-meta text-center mb-6 px-2"
              >
                Bitte beachten Sie, dass Rückerstattungen je nach Zahlungsanbieter etwas Zeit in Anspruch nehmen können.
              </motion.p>
            )}

            <hr className="svh-rule mb-6" />

            {/* Actions */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.45 }}
              className="space-y-3"
            >
              <a href="/" className="svh-btn svh-btn--primary svh-focus w-full">
                Zur Startseite
              </a>
              <a href="/" className="svh-btn svh-btn--secondary svh-focus w-full">
                <Calendar className="w-4 h-4" />
                Neue Buchung erstellen
              </a>
            </motion.div>
          </div>
        </motion.div>

        <p className="svh-meta text-center mt-6">SV Heinersreuth e.V.</p>
      </motion.div>
    </div>
  );
}
